import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { cn } from 'helpers/classnames';
import useMartyContext from 'hooks/useMartyContext';
import FinalSale from 'components/common/FinalSale';
import MarketplaceAwareLink from 'components/common/MarketplaceAwareLink';
import { generateProductUrl, isItemInCancelState, isItemInReturnState } from 'helpers/MyAccountUtils';
import { IS_GIFT_CARD } from 'common/regex';
import { track } from 'apis/amethyst';
import { evWriteProductReviewClick } from 'events/productReview';
import { EXCHANGE_INITIATE_PAGE } from 'constants/amethystPageTypes';
import { evProductClickThrough } from 'events/account';
import { PAGE_TYPE_MAP } from 'helpers/analytics';
import { PRODUCT_LINK_TYPE } from 'constants/amethystEnums';
import { getStatusMap } from 'components/account/OrderStatus';
import { WRITE_A_REVIEW_CTA } from 'constants/reviews';

import css from 'styles/components/account/orderProductForExchanges.scss';

const { TEXT_LINK, IMAGE_LINK } = PRODUCT_LINK_TYPE;

const OrderProductForExchanges = props => {
  const {
    product,
    merchantId,
    price,
    isLegacy,
    finalSale,
    lineItemState,
    hideWriteReview,
    itemUniqueId,
    planName,
    email,
    deliveryDisplayDate,
    message,
    from
  } = props;
  const { attributes, retinaImageUrl, name, brand, style, reviewUrl, productId, styleId, stockId, asin } = product;
  const returnState = isItemInReturnState(lineItemState);
  const cancelState = isItemInCancelState(lineItemState);
  const isInSpecialState = returnState || cancelState;
  const { testId, marketplace } = useMartyContext();
  const {
    dataMaskPiiElements,
    features: { showReviews },
    checkout: { asinVerbiage = 'ASIN' }
  } = marketplace;
  const isGiftCard = IS_GIFT_CARD.test(brand);
  const symphonyStatuses = useSelector(state => state?.account?.content?.['order-status-descriptions']);
  const onProductClick = e => {
    const { productLinkType } = e.currentTarget.dataset;
    track(() => [
      evProductClickThrough,
      {
        productLinkType,
        product,
        sourcePage: PAGE_TYPE_MAP.orderInformation
      }
    ]);
  };
  const statusMap = getStatusMap(symphonyStatuses, planName, lineItemState);

  return (
    <div className={css.wrapper} data-test-id={testId('orderProduct')}>
      <MarketplaceAwareLink
        // Since we have a duplicate link wrapping both this purely decorative image and
        // The Brand/Product itself, we can safely hide this from screen readers/keyboard nav
        aria-hidden={true}
        merchantId={merchantId}
        tabIndex={-1}
        to={generateProductUrl(product, isLegacy)}
        className={css.mProduct}
        onClick={onProductClick}
        data-product-link-type={IMAGE_LINK}
        data-track-action="My-Favorites"
        data-track-label="Favorites-Product"
        data-track-value="Image-Clicked"
        data-test-id={testId('productImageLink')}
      >
        <div
          className={cn(css.image, isInSpecialState && css.specialState, returnState && css.returnedState, cancelState && css.cancelledState)}
          data-cs-mask={dataMaskPiiElements}
        >
          {isInSpecialState && <span>{statusMap?.title}</span>}
          <img src={retinaImageUrl} alt={name} />
        </div>
      </MarketplaceAwareLink>

      <div id={`aria-orderProductDetails-${itemUniqueId}`} className={css.itemContainer}>
        <MarketplaceAwareLink
          merchantId={merchantId}
          className={css.productTitle}
          to={generateProductUrl(product, isLegacy)}
          onClick={onProductClick}
          data-product-link-type={TEXT_LINK}
          data-test-id={testId('productNameLink')}
          data-cs-mask={dataMaskPiiElements}
        >
          <p className="zappos:body-m" data-test-id={testId('productBrand')}>
            {brand}
          </p>
          <p className={cn(css.productName, 'zappos:body-m-bold')} data-test-id={testId('productStyle')}>
            {style}
          </p>
        </MarketplaceAwareLink>
        <div data-cs-mask={dataMaskPiiElements} className={cn(css.itemPrice, 'zappos:body-m')} data-test-id={testId('productPrice')}>
          {price}
        </div>
        <div className={css.itemDetails}>
          <div data-cs-mask={dataMaskPiiElements}>
            {!isGiftCard &&
              attributes.map(({ key, value }) => (
                <dl key={key}>
                  <dt className="zappos:body-m">{key === 'ASIN' ? asinVerbiage : key}: </dt>
                  <dd className="zappos:body-m" data-test-id={testId(`attribute-${key}`)}>
                    {value}
                  </dd>
                </dl>
              ))}
            {message && (
              <dl className="flex flex-wrap">
                <dt className="zappos:body-m">Message:&nbsp;</dt>
                <dd className="m-0 zappos:body-m" data-test-id={testId('attribute-message')}>
                  {message}
                </dd>
              </dl>
            )}
            {email && (
              <dl className="flex flex-wrap">
                <dt className="zappos:body-m">Email To:&nbsp;</dt>
                <dd className="m-0 zappos:body-m" data-test-id={testId('attribute-email')}>
                  {email}
                </dd>
              </dl>
            )}
            {from && (
              <dl className="flex flex-wrap">
                <dt className="zappos:body-m">From:&nbsp;</dt>
                <dd className="m-0 zappos:body-m" data-test-id={testId('attribute-from')}>
                  {from}
                </dd>
              </dl>
            )}
            {deliveryDisplayDate && (
              <div className="flex flex-wrap">
                <dt className="zappos:body-m">Deliver On:&nbsp;</dt>
                <dd className="m-0 zappos:body-m" data-test-id={testId('attribute-delivery-date')}>
                  {deliveryDisplayDate}
                </dd>
              </div>
            )}
            {finalSale && <FinalSale />}
          </div>
          {showReviews && reviewUrl && !hideWriteReview && (
            <p className={css.reviewUrl} data-test-id={testId('productReviewLink')}>
              <Link
                className="zappos:!body-m-medium"
                to={reviewUrl}
                onClick={() => {
                  track(() => [
                    evWriteProductReviewClick,
                    {
                      productId,
                      styleId,
                      stockId,
                      asin,
                      addedFrom: EXCHANGE_INITIATE_PAGE
                    }
                  ]);
                }}
              >
                {WRITE_A_REVIEW_CTA}
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

OrderProductForExchanges.propTypes = {
  product: PropTypes.object.isRequired,
  price: PropTypes.string
};

export default OrderProductForExchanges;
